import React, { useState } from "react";
import { ShieldAlert, ScrollText, Info, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { Panel } from "@/components/common/Panel";
import { SeverityBadge, ProvenanceBadge } from "@/components/common/Badges";
import { FunctionDetailBlock } from "@/components/roles/FunctionDrilldown";

/** Maps a rulebook matrix side ({function_id,label,tcodes,auth_objects,…})
 * onto the FunctionDetailBlock shape shared with the SoD drill-downs. */
const toFn = (side) => ({
    function_id: side.function_id,
    name: side.label,
    bp: "",
    actions: side.tcodes || [],
    objects: side.auth_objects || [],
    object_fields: side.object_fields || {},
    object_sources: side.object_sources || {},
    provenance: side.provenance,
    enrichment: side.enrichment || {},
});

export function SoDRulebookPanel({
    caps, rulebook, showRulebook, setShowRulebook, showCritical, setShowCritical,
    bpFilter, setBpFilter, rulebookSearch, setRulebookSearch, filteredMatrix,
}) {
    const [expandedRisk, setExpandedRisk] = useState(null);
    const [expandedCritical, setExpandedCritical] = useState(null);
    const [enrichmentOpen, setEnrichmentOpen] = useState(false);
    return (
        <>
            {!caps?.is_admin && (
                <Panel eyebrow="Active SoD Rulebook" title="Restricted">
                    <div data-testid="rulebook-restricted" className="flex items-start gap-3 py-2">
                        <ShieldAlert size={18} className="text-amber-400 shrink-0 mt-0.5" />
                        <p className="text-sm text-slate-400">
                            The detailed SoD Rulebook (Tcode / Authorization Object / field-value level) is
                            <span className="text-amber-300"> Admin-only</span>. Ask a platform admin to review rule
                            details, or continue using Mass Generation and the Role Library below — every generated
                            role is still checked against the full ruleset automatically.
                        </p>
                    </div>
                </Panel>
            )}
            {rulebook && (
                <Panel
                    eyebrow="Active SoD Rulebook"
                    title={`${rulebook.ruleset.name} · v${rulebook.ruleset.version}`}
                    action={
                        <button
                            data-testid="rulebook-toggle"
                            onClick={() => setShowRulebook((v) => !v)}
                            className="inline-flex items-center gap-1.5 text-xs font-mono text-cyan-400 hover:text-cyan-300 transition-colors"
                        >
                            <ScrollText size={13} />
                            {showRulebook ? "Hide" : "View"} {rulebook.rule_count} rules
                        </button>
                    }
                >
                    <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono mb-3">
                        <span className="px-1.5 py-0.5 rounded-sm bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">{rulebook.rule_count} access risks</span>
                        <span className="px-1.5 py-0.5 rounded-sm bg-rose-500/10 text-rose-300 border border-rose-500/20">{rulebook.critical_activity_count} critical activities</span>
                        <span className="px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">granularity: {rulebook.ruleset.granularity}</span>
                        {rulebook.business_processes.map((bp) => (
                            <span key={bp} className="px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">{bp}</span>
                        ))}
                    </div>
                    <div className="flex items-start gap-2 text-[11px] text-amber-200/90 bg-amber-500/10 border border-amber-500/25 rounded-sm p-2.5">
                        <Info size={14} className="text-amber-400 shrink-0 mt-0.5" />
                        <span>{rulebook.ruleset.source}</span>
                    </div>
                    {rulebook.ruleset.data_quality && rulebook.ruleset.data_quality.unfireable_function_count > 0 && (
                        <div data-testid="ruleset-data-quality-note" className="flex items-start gap-2 text-[11px] text-rose-200/90 bg-rose-500/10 border border-rose-500/25 rounded-sm p-2.5 mt-2">
                            <ShieldAlert size={14} className="text-rose-400 shrink-0 mt-0.5" />
                            <span>
                                <strong className="text-rose-300">Coverage gap:</strong>{" "}
                                {rulebook.ruleset.data_quality.note}
                            </span>
                        </div>
                    )}
                    {rulebook.ruleset.enrichment?.enabled && (
                        <div data-testid="ruleset-enrichment-note" className="text-[11px] text-violet-200/90 bg-violet-500/10 border border-violet-500/25 rounded-sm p-2.5 mt-2">
                            <div className="flex items-start gap-2">
                                <Sparkles size={14} className="text-violet-400 shrink-0 mt-0.5" />
                                <span className="flex-1">
                                    <strong className="text-violet-300">
                                        Enrichment overlay active · {rulebook.ruleset.enrichment.version}
                                    </strong>{" "}
                                    — {rulebook.ruleset.enrichment.enriched_function_count ?? 0} function(s) enriched,
                                    activating {rulebook.ruleset.enrichment.activated_risk_count ?? 0} previously dormant SoD risk(s).
                                </span>
                                <button
                                    data-testid="ruleset-enrichment-toggle"
                                    onClick={() => setEnrichmentOpen((o) => !o)}
                                    className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-violet-300 hover:text-violet-200 underline decoration-dotted"
                                >
                                    {enrichmentOpen ? "Hide detail" : "Why?"}
                                </button>
                            </div>
                            {enrichmentOpen && (
                                <p data-testid="ruleset-enrichment-detail" className="mt-2 pl-6 leading-relaxed text-violet-100/80">
                                    {rulebook.ruleset.enrichment.note}
                                </p>
                            )}
                        </div>
                    )}

                    {showRulebook && (
                        <>
                            <div className="flex flex-wrap items-center gap-2 mt-4 mb-3">
                                <input
                                    data-testid="rulebook-search-input"
                                    value={rulebookSearch}
                                    onChange={(e) => setRulebookSearch(e.target.value)}
                                    placeholder="Search risk id, name or function…"
                                    className="flex-1 min-w-[200px] bg-[var(--bg-base)] border border-white/10 rounded-sm px-3 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                                />
                                <select
                                    data-testid="rulebook-bp-filter"
                                    value={bpFilter}
                                    onChange={(e) => setBpFilter(e.target.value)}
                                    className="bg-[var(--bg-base)] border border-white/10 rounded-sm px-2 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                                >
                                    <option value="all">All business processes</option>
                                    {rulebook.business_processes.map((bp) => (
                                        <option key={bp} value={bp}>{bp}</option>
                                    ))}
                                </select>
                                <span className="text-[11px] font-mono text-slate-500">{filteredMatrix.length} shown</span>
                            </div>
                            <div className="overflow-x-auto max-h-[420px] overflow-y-auto border border-white/5 rounded-sm">
                                <table className="tbl w-full text-xs">
                                    <thead className="sticky top-0 bg-[var(--bg-base)] z-10">
                                        <tr>
                                            <th>Risk ID</th><th>Business Process</th><th>Risk</th>
                                            <th>Function A ↔ Function B</th><th>Severity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredMatrix.map((r) => {
                                            const isOpen = expandedRisk === r.id;
                                            return (
                                            <React.Fragment key={r.id}>
                                            <tr
                                                data-testid={`rulebook-row-${r.id}`}
                                                title={r.business_impact || undefined}
                                                onClick={() => setExpandedRisk(isOpen ? null : r.id)}
                                                className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                                            >
                                                <td className="font-mono text-cyan-300">
                                                    <span className="inline-flex items-center gap-1">
                                                        {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}{r.id}
                                                    </span>
                                                </td>
                                                <td className="text-slate-400">{r.business_process}</td>
                                                <td className="text-slate-200">{r.name}</td>
                                                <td className="font-mono text-[10px] text-slate-500">{r.a.label} ↔ {r.b.label}</td>
                                                <td>
                                                    <div className="flex items-center gap-1.5">
                                                        <SeverityBadge severity={r.severity} />
                                                        <ProvenanceBadge
                                                            provenance={r.provenance}
                                                            enrichment={r.a.enrichment || r.b.enrichment}
                                                            testId={`rulebook-provenance-${r.id}`}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                            {isOpen && (
                                                <tr data-testid={`rulebook-drilldown-${r.id}`}>
                                                    <td colSpan={5} className="bg-[#05050A]/60">
                                                        <div className="p-2 space-y-2">
                                                            {r.business_impact && (
                                                                <p className="text-[11px] text-amber-200/80"><strong className="text-amber-300">Business impact:</strong> {r.business_impact}</p>
                                                            )}
                                                            <div className="grid md:grid-cols-2 gap-2">
                                                                <FunctionDetailBlock fn={toFn(r.a)} />
                                                                <FunctionDetailBlock fn={toFn(r.b)} />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex items-center justify-between mt-4 mb-2">
                                <div className="label-eyebrow">Critical Activities (standalone, no pairing required)</div>
                                <button
                                    data-testid="critical-activities-toggle"
                                    onClick={() => setShowCritical((v) => !v)}
                                    className="text-xs font-mono text-rose-400 hover:text-rose-300 transition-colors"
                                >
                                    {showCritical ? "Hide" : "View"} {rulebook.critical_activity_count}
                                </button>
                            </div>
                            {showCritical && (
                                <div className="overflow-x-auto max-h-[300px] overflow-y-auto border border-white/5 rounded-sm">
                                    <table className="tbl w-full text-xs">
                                        <thead className="sticky top-0 bg-[var(--bg-base)] z-10">
                                            <tr><th>Risk ID</th><th>Business Process</th><th>Critical Function</th><th>Severity</th></tr>
                                        </thead>
                                        <tbody>
                                            {rulebook.critical_activities.map((c) => {
                                                const isOpen = expandedCritical === c.id;
                                                return (
                                                <React.Fragment key={c.id}>
                                                <tr
                                                    data-testid={`rulebook-critical-row-${c.id}`}
                                                    title={c.business_impact || undefined}
                                                    onClick={() => setExpandedCritical(isOpen ? null : c.id)}
                                                    className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                                                >
                                                    <td className="font-mono text-rose-300">
                                                        <span className="inline-flex items-center gap-1">
                                                            {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}{c.id}
                                                        </span>
                                                    </td>
                                                    <td className="text-slate-400">{c.business_process}</td>
                                                    <td className="text-slate-200">{c.function.label}</td>
                                                    <td>
                                                        <div className="flex items-center gap-1.5">
                                                            <SeverityBadge severity={c.severity} />
                                                            <ProvenanceBadge
                                                                provenance={c.provenance}
                                                                enrichment={c.function.enrichment}
                                                                testId={`rulebook-critical-provenance-${c.id}`}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                                {isOpen && (
                                                    <tr data-testid={`rulebook-critical-drilldown-${c.id}`}>
                                                        <td colSpan={4} className="bg-[#05050A]/60">
                                                            <div className="p-2 space-y-2">
                                                                {c.business_impact && (
                                                                    <p className="text-[11px] text-amber-200/80"><strong className="text-amber-300">Business impact:</strong> {c.business_impact}</p>
                                                                )}
                                                                <FunctionDetailBlock fn={toFn(c.function)} />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </Panel>
            )}
        </>
    );
}
